import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Flex, Form, Input, Progress, Segmented, Select, Table, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import {
  listStockCounts,
  listStockLocations,
  startStockCount,
  type StockCountListItem,
  type StockCountStatus,
} from './counts-api';

const { Text, Title } = Typography;

const STATUSES: StockCountStatus[] = ['COUNTING', 'SUBMITTED', 'APPROVED', 'CANCELLED'];

/** Stock counts: what is being counted, what waits for a manager, what is done. */
export function StockCountsPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StockCountStatus>('COUNTING');
  const [open, setOpen] = useState(false);
  const [locationUid, setLocationUid] = useState<string | undefined>(undefined);
  const [codePrefix, setCodePrefix] = useState('');
  const [note, setNote] = useState('');

  const list = useInfiniteQuery({
    queryKey: ['stock-counts', status],
    queryFn: ({ pageParam }) => listStockCounts({ status, ...(pageParam !== undefined ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];

  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: listStockLocations, enabled: open });

  const start = useMutation({
    mutationFn: () =>
      startStockCount({
        locationUid: locationUid ?? '',
        ...(codePrefix.trim().length > 0 ? { codePrefix: codePrefix.trim() } : {}),
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      }),
    onSuccess: (count) => {
      setOpen(false);
      void navigate(`/parts/counts/${count.uid}`);
    },
  });

  const openDrawer = (): void => {
    start.reset();
    setLocationUid(undefined);
    setCodePrefix('');
    setNote('');
    setOpen(true);
  };

  const startButton = (
    <Button type="primary" size="large" onClick={openDrawer}>
      {t('counts.start')}
    </Button>
  );

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Title level={3} style={{ margin: 0 }}>
          {t('nav.stockCounts')}
        </Title>
        {startButton}
      </Flex>

      <Segmented<StockCountStatus>
        value={status}
        onChange={setStatus}
        size="large"
        block
        options={STATUSES.map((s) => ({ value: s, label: t(`counts.status.${s}`) }))}
      />

      {list.error !== null && (
        <Alert
          type="error"
          showIcon
          message={list.error instanceof ApiError ? list.error.message : t('counts.loadFailed')}
        />
      )}

      <Table<StockCountListItem>
        dataSource={rows}
        rowKey={(r) => r.uid}
        loading={list.isFetching}
        pagination={false}
        locale={{
          emptyText: (
            <Empty description={t('counts.empty')}>
              {startButton}
            </Empty>
          ),
        }}
        columns={[
          {
            title: t('counts.columns.count'),
            key: 'count',
            render: (_: unknown, r) => (
              <Flex vertical gap={2}>
                <Link to={`/parts/counts/${r.uid}`} className="tabular">
                  {r.countNo}
                </Link>
                <Text type="secondary">
                  {r.locationName}
                  {r.codePrefix !== null ? ` · ${r.codePrefix}` : ''}
                </Text>
              </Flex>
            ),
          },
          {
            title: t('counts.columns.progress'),
            key: 'progress',
            render: (_: unknown, r) => (
              <Flex vertical gap={2}>
                <Text>{t('counts.progress', { counted: r.progress.counted, total: r.progress.total })}</Text>
                <Progress
                  percent={r.progress.total === 0 ? 0 : Math.round((r.progress.counted / r.progress.total) * 100)}
                  showInfo={false}
                  size="small"
                />
              </Flex>
            ),
          },
          {
            title: t('counts.columns.started'),
            key: 'started',
            responsive: ['md' as const],
            render: (_: unknown, r) => (
              <Flex vertical gap={2}>
                <Text>{formatDateTime(r.createdAt)}</Text>
                <Text type="secondary">{r.createdBy ?? ''}</Text>
              </Flex>
            ),
          },
          {
            title: '',
            key: 'status',
            align: 'right' as const,
            responsive: ['md' as const],
            render: (_: unknown, r) => <Tag>{t(`counts.status.${r.status}`)}</Tag>,
          },
        ]}
      />
      {list.hasNextPage && (
        <Flex justify="center">
          <Button
            size="large"
            loading={list.isFetchingNextPage}
            onClick={() => {
              void list.fetchNextPage();
            }}
          >
            {t('counts.more')}
          </Button>
        </Flex>
      )}

      <ActionDrawer
        open={open}
        title={t('counts.startTitle')}
        submitLabel={t('counts.start')}
        submitting={start.isPending}
        submitDisabled={locationUid === undefined}
        onClose={() => {
          setOpen(false);
        }}
        onSubmit={() => {
          start.mutate();
        }}
        consequence={t('counts.startConsequence')}
        error={
          start.error === null ? undefined : (
            <Alert
              type="error"
              showIcon
              message={start.error instanceof ApiError ? start.error.message : t('counts.startFailed')}
            />
          )
        }
      >
        <Form layout="vertical">
          <Form.Item label={t('counts.fields.location')} htmlFor="count-location" required>
            <Select
              id="count-location"
              size="large"
              value={locationUid}
              loading={locations.isLoading}
              onChange={setLocationUid}
              options={(locations.data ?? []).map((l) => ({ value: l.uid, label: l.name }))}
            />
          </Form.Item>
          <Form.Item label={t('counts.fields.prefix')} htmlFor="count-prefix" help={t('counts.prefixHelp')}>
            <Input
              id="count-prefix"
              size="large"
              value={codePrefix}
              onChange={(e) => {
                setCodePrefix(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item label={t('counts.fields.note')} htmlFor="count-note">
            <Input.TextArea
              id="count-note"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </Flex>
  );
}
